import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Spinner, Alert, Modal, Nav, Tab, Form, Badge, Collapse, Row, Col } from 'react-bootstrap';
import { FiAlertTriangle, FiRefreshCw, FiEye, FiCheckCircle, FiMail, FiChevronDown, FiChevronUp, FiMessageSquare, FiBell } from 'react-icons/fi';
import {
    sendForecastTriggeredBulkEmail,
    sendForecastTriggeredBulkSms,
    getAllPartners,
    sendTripsClientsBankDetailsRecapEmail,
    sendAloaEndOfStayNotifications,
    getAloaEndOfStayPreview,
    getLacdesgravesEndOfStayPreview,
    sendLacdesgravesEndOfStayRecapEmail,
    sendStandardEndOfStayRecapEmail,
    sendTripsClientsBankDetailsRemindersEmail,
    sendStandardTripsClientsBankDetailsRemindersEmail,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import SmsMonitorTab from '../../components/Admin/SmsMonitorTab';
import EmailMonitorTab from '../../components/Admin/EmailMonitorTab';
import StandardForecastTriggerEmailTab from '../../components/Admin/StandardForecastTriggerEmailTab';
import StandardForecastNotTriggeredEmailTab from '../../components/Admin/StandardForecastNotTriggeredEmailTab';
import StandardObservationTriggerEmailTab from '../../components/Admin/StandardObservationTriggerEmailTab';
import StandardObservationNotTriggeredEmailTab from '../../components/Admin/StandardObservationNotTriggeredEmailTab';
import BankDetailsRemindersResult from '../../components/Admin/BankDetailsRemindersResult';
import BankDetailsRemindersErrorAlert from '../../components/Admin/BankDetailsRemindersErrorAlert';
import TriggeredRecapResult from '../../components/Admin/TriggeredRecapResult';
import {
    buildLocalPeriodOrderError,
    buildLocalTripEndPeriodError,
    formatDateDdMmYyyy,
    getLatestAllowedReminderDateEnd,
    getParisTodayIso,
    isReminderDateAfterLatestAllowed,
    parseBankDetailsRemindersError,
} from '../../utils/bankDetailsRemindersError';
import {
    getTriggeredRecapFieldError,
    isTriggeredRecapResponse,
    parseTriggeredRecapApiError,
} from '../../utils/triggeredRecapResult';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsDaily.css';

const END_OF_STAY_RECAP_TYPE = {
    TRIGGERED_ONLY: 'triggered-only',
    TRIGGERED_AND_NOT: 'triggered-and-not',
};

const END_OF_STAY_PROVIDER = {
    ALOA: 'aloa',
    LAC_DES_GRAVES: 'lac-des-graves',
};

const MAIN_GROUPS = {
    FORECASTS: 'forecasts',
    OBSERVATIONS: 'observations',
};

const FORECAST_GROUPS = {
    TRIGGER: 'trigger',
    NOT_TRIGGER: 'not-trigger',
    END_OF_STAY: 'end-of-stay',
    RIB: 'rib',
    SMS_MONITOR: 'sms-monitor',
    EMAIL_MONITOR: 'email-monitor',
};

const FORECAST_VARIANTS = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const SLOT_STATUS = {
    AVAILABLE: 'available',
    SOON: 'soon',
};

/** Mapping groupe × variante → onglet contenu + statut badge */
const FORECAST_SLOT_MAP = {
    [FORECAST_GROUPS.TRIGGER]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-forecast-trigger-email',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'today-triggered',
            status: SLOT_STATUS.AVAILABLE,
        },
    },
    [FORECAST_GROUPS.NOT_TRIGGER]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-forecast-not-triggered-email',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'not-trigger-specific',
            status: SLOT_STATUS.SOON,
        },
    },
    [FORECAST_GROUPS.END_OF_STAY]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-end-of-stay-recap',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'end-of-stay-recap',
            status: SLOT_STATUS.AVAILABLE,
        },
    },
    [FORECAST_GROUPS.RIB]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-bank-details-reminders',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'bank-details-reminders',
            status: SLOT_STATUS.AVAILABLE,
        },
    },
    [FORECAST_GROUPS.SMS_MONITOR]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'sms-monitor',
            status: SLOT_STATUS.AVAILABLE,
            standalone: true,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'sms-monitor',
            status: SLOT_STATUS.AVAILABLE,
            standalone: true,
        },
    },
    [FORECAST_GROUPS.EMAIL_MONITOR]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'email-monitor',
            status: SLOT_STATUS.AVAILABLE,
            standalone: true,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'email-monitor',
            status: SLOT_STATUS.AVAILABLE,
            standalone: true,
        },
    },
};

const FORECAST_GROUP_LIST = [
    FORECAST_GROUPS.TRIGGER,
    FORECAST_GROUPS.NOT_TRIGGER,
    FORECAST_GROUPS.END_OF_STAY,
    FORECAST_GROUPS.RIB,
    FORECAST_GROUPS.SMS_MONITOR,
    FORECAST_GROUPS.EMAIL_MONITOR,
];

const OBSERVATION_GROUPS = {
    TRIGGER: 'trigger',
    NOT_TRIGGER: 'not-trigger',
};

const OBSERVATION_SLOT_MAP = {
    [OBSERVATION_GROUPS.TRIGGER]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-observation-trigger-email',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'observation-trigger-specific',
            status: SLOT_STATUS.SOON,
        },
    },
    [OBSERVATION_GROUPS.NOT_TRIGGER]: {
        [FORECAST_VARIANTS.STANDARD]: {
            tab: 'standard-observation-not-triggered-email',
            status: SLOT_STATUS.AVAILABLE,
        },
        [FORECAST_VARIANTS.SPECIFIC]: {
            tab: 'observation-not-trigger-specific',
            status: SLOT_STATUS.SOON,
        },
    },
};

const OBSERVATION_GROUP_LIST = [
    OBSERVATION_GROUPS.TRIGGER,
    OBSERVATION_GROUPS.NOT_TRIGGER,
];

const resolveForecastSlot = (group, variant) => (
    FORECAST_SLOT_MAP[group]?.[variant] || FORECAST_SLOT_MAP[FORECAST_GROUPS.TRIGGER][FORECAST_VARIANTS.STANDARD]
);

const resolveObservationSlot = (group, variant) => (
    OBSERVATION_SLOT_MAP[group]?.[variant]
    || OBSERVATION_SLOT_MAP[OBSERVATION_GROUPS.TRIGGER][FORECAST_VARIANTS.STANDARD]
);

const isBulkSendSuccess = (result) => {
    if (!result || typeof result !== 'object') return false;
    if (result.success === false) return false;
    if (result.success === true) return true;
    return result.status === 'success';
};

const formatBulkSendError = (result) => {
    const parts = [];
    if (result?.message) {
        parts.push(result.message);
    }
    if (Array.isArray(result?.errors)) {
        result.errors.forEach((err) => {
            if (err?.detail && !parts.includes(err.detail)) {
                parts.push(err.detail);
            } else if (err?.code && !parts.some((p) => p.includes(err.code))) {
                parts.push(err.code);
            }
        });
    }
    return parts.length > 0 ? parts.join(' — ') : null;
};

const extractBulkSendApiError = (error, defaultMessage) => {
    const data = error?.response?.data;
    const status = error?.response?.status;
    const detail = data?.detail;

    if (status === 403) {
        const detailText = typeof detail === 'string'
            ? detail
            : (detail?.message || detail?.msg || '');
        if (
            /permission|performe action|forbidden|accès/i.test(detailText)
            || !detailText
        ) {
            return null; // resolved with i18n by caller via forbidden key
        }
        return detailText;
    }

    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    if (detail && typeof detail === 'object' && (detail.message || detail.msg)) {
        return detail.message || detail.msg;
    }
    if (data && typeof data === 'object') {
        return formatBulkSendError(data) || handleApiError(error, defaultMessage);
    }
    return handleApiError(error, defaultMessage);
};

const AdminTripsDaily = () => {
    const { t } = useTranslation();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);

    const [mainGroup, setMainGroup] = useState(MAIN_GROUPS.FORECASTS);
    const [forecastGroup, setForecastGroup] = useState(FORECAST_GROUPS.TRIGGER);
    const [forecastVariant, setForecastVariant] = useState(FORECAST_VARIANTS.STANDARD);
    const [observationGroup, setObservationGroup] = useState(OBSERVATION_GROUPS.TRIGGER);
    const [observationVariant, setObservationVariant] = useState(FORECAST_VARIANTS.STANDARD);
    const [activeTab, setActiveTab] = useState('standard-forecast-trigger-email');

    const currentForecastSlot = useMemo(
        () => resolveForecastSlot(forecastGroup, forecastVariant),
        [forecastGroup, forecastVariant]
    );

    const currentObservationSlot = useMemo(
        () => resolveObservationSlot(observationGroup, observationVariant),
        [observationGroup, observationVariant]
    );

    const currentSlot = mainGroup === MAIN_GROUPS.OBSERVATIONS
        ? currentObservationSlot
        : currentForecastSlot;

    const selectForecastSlot = (group, variant) => {
        const slot = resolveForecastSlot(group, variant);
        setForecastGroup(group);
        setForecastVariant(variant);
        setActiveTab(slot.tab);
    };

    const selectObservationSlot = (group, variant) => {
        const slot = resolveObservationSlot(group, variant);
        setObservationGroup(group);
        setObservationVariant(variant);
        setActiveTab(slot.tab);
    };

    const selectMainGroup = (group) => {
        setMainGroup(group);
        if (group === MAIN_GROUPS.OBSERVATIONS) {
            selectObservationSlot(observationGroup, observationVariant);
        } else {
            selectForecastSlot(forecastGroup, forecastVariant);
        }
    };

    const [selectedPartnerNames, setSelectedPartnerNames] = useState([]);
    const [partnerSearch, setPartnerSearch] = useState('');
    const [forecastPartners, setForecastPartners] = useState([]);
    const [forecastPartnersLoading, setForecastPartnersLoading] = useState(false);
    const [forecastPartnersError, setForecastPartnersError] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [emailResult, setEmailResult] = useState(null);
    const [smsLoading, setSmsLoading] = useState(false);
    const [smsError, setSmsError] = useState(null);
    const [smsResult, setSmsResult] = useState(null);

    const [endOfStayRecapType, setEndOfStayRecapType] = useState(END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY);
    const [endOfStayProvider, setEndOfStayProvider] = useState(END_OF_STAY_PROVIDER.ALOA);
    const [endOfStayTripEndFrom, setEndOfStayTripEndFrom] = useState('');
    const [endOfStayTripEndTo, setEndOfStayTripEndTo] = useState('');
    const [endOfStayCompanyName, setEndOfStayCompanyName] = useState('');
    const [endOfStayPartnerSearch, setEndOfStayPartnerSearch] = useState('');
    const [endOfStayLoading, setEndOfStayLoading] = useState(false);
    const [endOfStayPreviewLoading, setEndOfStayPreviewLoading] = useState(false);
    const [endOfStayError, setEndOfStayError] = useState(null);
    const [endOfStayPreviewError, setEndOfStayPreviewError] = useState(null);
    const [endOfStayResult, setEndOfStayResult] = useState(null);
    const [endOfStayPreviewResult, setEndOfStayPreviewResult] = useState(null);
    const [showEndOfStayEndpoint, setShowEndOfStayEndpoint] = useState(false);
    const [showTriggeredRecapConfirm, setShowTriggeredRecapConfirm] = useState(false);
    const [endOfStayFieldErrors, setEndOfStayFieldErrors] = useState({});

    const [standardEosTripEndFrom, setStandardEosTripEndFrom] = useState('');
    const [standardEosTripEndTo, setStandardEosTripEndTo] = useState('');
    const [standardEosCompanyName, setStandardEosCompanyName] = useState('');
    const [standardEosPartnerSearch, setStandardEosPartnerSearch] = useState('');
    const [standardEosLoading, setStandardEosLoading] = useState(false);
    const [standardEosError, setStandardEosError] = useState(null);
    const [standardEosResult, setStandardEosResult] = useState(null);
    const [standardEosFieldErrors, setStandardEosFieldErrors] = useState({});
    const [showStandardEosConfirm, setShowStandardEosConfirm] = useState(false);

    const STANDARD_EOS_PREFIX = 'pages.adminTripsDaily.standardEndOfStayRecap';

    const [reminderDateStart, setReminderDateStart] = useState('');
    const [reminderDateEnd, setReminderDateEnd] = useState('');
    const [reminderCompanyName, setReminderCompanyName] = useState('');
    const [reminderPartnerSearch, setReminderPartnerSearch] = useState('');
    const [reminderEmailLoading, setReminderEmailLoading] = useState(false);
    const [reminderEmailError, setReminderEmailError] = useState(null);
    const [reminderEmailResult, setReminderEmailResult] = useState(null);

    const [standardReminderDateStart, setStandardReminderDateStart] = useState('');
    const [standardReminderDateEnd, setStandardReminderDateEnd] = useState('');
    const [standardReminderCompanyName, setStandardReminderCompanyName] = useState('');
    const [standardReminderPartnerSearch, setStandardReminderPartnerSearch] = useState('');
    const [standardReminderEmailLoading, setStandardReminderEmailLoading] = useState(false);
    const [standardReminderEmailError, setStandardReminderEmailError] = useState(null);
    const [standardReminderEmailResult, setStandardReminderEmailResult] = useState(null);
    const [showStandardReminderDetails, setShowStandardReminderDetails] = useState(false);

    const STANDARD_REMINDER_PREFIX = 'pages.adminTripsDaily.standardBankDetailsReminders';

    const partnerCompanyOptions = useMemo(() => {
        const names = forecastPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [forecastPartners]);

    const filteredPartnerOptions = useMemo(() => {
        const query = partnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, partnerSearch]);

    const filteredStandardEosPartnerOptions = useMemo(() => {
        const query = standardEosPartnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, standardEosPartnerSearch]);

    const filteredEndOfStayPartnerOptions = useMemo(() => {
        const query = endOfStayPartnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, endOfStayPartnerSearch]);

    const filteredReminderPartnerOptions = useMemo(() => {
        const query = reminderPartnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, reminderPartnerSearch]);

    const filteredStandardReminderPartnerOptions = useMemo(() => {
        const query = standardReminderPartnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, standardReminderPartnerSearch]);

    useEffect(() => {
        const loadForecastPartners = async () => {
            try {
                setForecastPartnersLoading(true);
                setForecastPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setForecastPartners(partnersArray);
            } catch (error) {
                setForecastPartners([]);
                setForecastPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setForecastPartnersLoading(false);
            }
        };

        if (isAuthenticated) {
            loadForecastPartners();
        }
    }, [isAuthenticated]);

    const handleSendEmail = async () => {
        try {
            setEmailLoading(true);
            setEmailError(null);
            setEmailResult(null);
            const result = await sendForecastTriggeredBulkEmail(selectedPartnerNames);
            if (!isBulkSendSuccess(result)) {
                setEmailError(formatBulkSendError(result) || t('pages.adminTripsDaily.error'));
                setEmailResult(null);
                return;
            }
            setEmailResult(result);
            setEmailError(null);
            setShowEmailModal(false);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                const parsed = extractBulkSendApiError(error, null);
                setEmailError(parsed || t('pages.adminTripsDaily.errors.forbiddenBulkEmail'));
            } else {
                setEmailError(
                    extractBulkSendApiError(error, t('pages.adminTripsDaily.error'))
                    || t('pages.adminTripsDaily.error')
                );
            }
        } finally {
            setEmailLoading(false);
        }
    };

    const handleSendSms = async () => {
        const defaultSmsError = 'Erreur lors de l\'envoi des SMS forecast-triggered';
        try {
            setSmsLoading(true);
            setSmsError(null);
            setSmsResult(null);
            const result = await sendForecastTriggeredBulkSms(selectedPartnerNames);
            if (!isBulkSendSuccess(result)) {
                setSmsError(formatBulkSendError(result) || defaultSmsError);
                setSmsResult(null);
                return;
            }
            setSmsResult(result);
            setSmsError(null);
            setShowSmsModal(false);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                const parsed = extractBulkSendApiError(error, null);
                setSmsError(parsed || t('pages.adminTripsDaily.errors.forbiddenBulkSms'));
            } else {
                setSmsError(extractBulkSendApiError(error, defaultSmsError));
            }
        } finally {
            setSmsLoading(false);
        }
    };

    const buildEndOfStayDateParams = () => {
        const params = {};
        if (endOfStayTripEndFrom && endOfStayTripEndFrom.trim() !== '') {
            params.trip_end_date_from = endOfStayTripEndFrom.trim();
        }
        if (endOfStayTripEndTo && endOfStayTripEndTo.trim() !== '') {
            params.trip_end_date_to = endOfStayTripEndTo.trim();
        }
        return params;
    };

    const endOfStayEndpointLabel = useMemo(() => {
        if (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY) {
            return 'GET /api/trips/clients/bank-details/send-triggered-recap-email';
        }
        if (endOfStayProvider === END_OF_STAY_PROVIDER.LAC_DES_GRAVES) {
            return 'POST /api/trips/lacdesgraves/clients/bank-details/send-triggered-and-not-triggered-recap-email';
        }
        return 'POST /api/trips/clients/bank-details/send-triggered-and-not-triggered-recap-email';
    }, [endOfStayRecapType, endOfStayProvider]);

    const endOfStaySupportsPreview = endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT;

    const endOfStayConfirmPrefix = endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT
        ? 'pages.adminTripsDaily.endOfStayRecap'
        : 'pages.adminTripsDaily.triggeredRecap';

    const applyStructuredEndOfStayResult = (result) => {
        setEndOfStayResult(result);
        setEndOfStayError(null);
        setEndOfStayFieldErrors({});
        if (!result?.success) {
            const fieldError = getTriggeredRecapFieldError(result);
            if (fieldError) {
                setEndOfStayFieldErrors({ [fieldError.field]: fieldError.reason });
            } else if (result.code === 'COMPANY_NOT_FOUND') {
                setEndOfStayFieldErrors({
                    company_name: result.message || t('pages.adminTripsDaily.triggeredRecap.companyNotFound'),
                });
            }
        }
    };

    const handleEndOfStaySend = async () => {
        try {
            setEndOfStayLoading(true);
            setEndOfStayError(null);
            setEndOfStayResult(null);
            const dateParams = buildEndOfStayDateParams();

            if (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY) {
                const params = { ...dateParams };
                if (endOfStayCompanyName && endOfStayCompanyName.trim() !== '') {
                    params.company_name = endOfStayCompanyName.trim();
                }
                const result = await sendTripsClientsBankDetailsRecapEmail(params);
                applyStructuredEndOfStayResult(result);
                return 'done';
            }

            const params = { ...dateParams };
            if (endOfStayCompanyName && endOfStayCompanyName.trim() !== '') {
                params.company_name = endOfStayCompanyName.trim();
            }

            if (endOfStayProvider === END_OF_STAY_PROVIDER.LAC_DES_GRAVES) {
                const result = await sendLacdesgravesEndOfStayRecapEmail(params);
                applyStructuredEndOfStayResult(result);
                return 'done';
            }

            const result = await sendAloaEndOfStayNotifications(params);
            applyStructuredEndOfStayResult(result);
            return 'done';
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                const parsed = extractBulkSendApiError(error, null);
                setEndOfStayError(parsed || t('pages.adminTripsDaily.errors.forbiddenRecap'));
                setEndOfStayResult(null);
                return 'forbidden';
            }
            const structured = parseTriggeredRecapApiError(error);
            if (
                structured
                && (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY
                    || endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT)
            ) {
                applyStructuredEndOfStayResult(structured);
            } else {
                setEndOfStayError(handleApiError(error, 'Erreur lors de l\'envoi du récap fin de séjour'));
            }
            return 'done';
        } finally {
            setEndOfStayLoading(false);
        }
    };

    const handleEndOfStayPreview = async () => {
        try {
            setEndOfStayPreviewLoading(true);
            setEndOfStayPreviewError(null);
            setEndOfStayPreviewResult(null);
            const dateParams = buildEndOfStayDateParams();
            const result = endOfStayProvider === END_OF_STAY_PROVIDER.LAC_DES_GRAVES
                ? await getLacdesgravesEndOfStayPreview(dateParams)
                : await getAloaEndOfStayPreview(dateParams);
            setEndOfStayPreviewResult(result);
        } catch (error) {
            setEndOfStayPreviewError(handleApiError(error, 'Erreur lors de l\'aperçu du récap fin de séjour'));
        } finally {
            setEndOfStayPreviewLoading(false);
        }
    };

    const resetEndOfStayForm = () => {
        setEndOfStayProvider(END_OF_STAY_PROVIDER.ALOA);
        setEndOfStayTripEndFrom('');
        setEndOfStayTripEndTo('');
        setEndOfStayCompanyName('');
        setEndOfStayPartnerSearch('');
        setEndOfStayError(null);
        setEndOfStayPreviewError(null);
        setEndOfStayResult(null);
        setEndOfStayPreviewResult(null);
        setEndOfStayFieldErrors({});
    };

    const buildStandardEosDateParams = () => {
        const params = {};
        if (standardEosTripEndFrom && standardEosTripEndFrom.trim() !== '') {
            params.trip_end_date_from = standardEosTripEndFrom.trim();
        }
        if (standardEosTripEndTo && standardEosTripEndTo.trim() !== '') {
            params.trip_end_date_to = standardEosTripEndTo.trim();
        }
        return params;
    };

    const applyStandardEosResult = (result) => {
        setStandardEosResult(result);
        setStandardEosError(null);
        setStandardEosFieldErrors({});
        if (!result?.success) {
            const fieldError = getTriggeredRecapFieldError(result);
            if (fieldError) {
                setStandardEosFieldErrors({ [fieldError.field]: fieldError.reason });
            } else if (result.code === 'COMPANY_NOT_FOUND') {
                setStandardEosFieldErrors({
                    company_name: result.message || t(`${STANDARD_EOS_PREFIX}.companyNotFound`),
                });
            }
        }
    };

    const handleStandardEosSend = async () => {
        try {
            setStandardEosLoading(true);
            setStandardEosError(null);
            setStandardEosResult(null);
            const params = buildStandardEosDateParams();
            if (standardEosCompanyName && standardEosCompanyName.trim() !== '') {
                params.company_name = standardEosCompanyName.trim();
            }
            const result = await sendStandardEndOfStayRecapEmail(params);
            applyStandardEosResult(result);
            return 'done';
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                const parsed = extractBulkSendApiError(error, null);
                setStandardEosError(parsed || t('pages.adminTripsDaily.errors.forbiddenRecap'));
                setStandardEosResult(null);
                return 'forbidden';
            }
            const structured = parseTriggeredRecapApiError(error);
            if (structured) {
                applyStandardEosResult(structured);
            } else {
                setStandardEosError(handleApiError(error, t(`${STANDARD_EOS_PREFIX}.error`)));
            }
            return 'done';
        } finally {
            setStandardEosLoading(false);
        }
    };

    const handleStandardEosFormSubmit = (e) => {
        e.preventDefault();
        setStandardEosFieldErrors({});
        if (
            standardEosTripEndFrom
            && standardEosTripEndTo
            && standardEosTripEndFrom.trim() > standardEosTripEndTo.trim()
        ) {
            setStandardEosFieldErrors({
                trip_end_date_to: t(`${STANDARD_EOS_PREFIX}.validation.dateOrder`),
            });
            return;
        }
        setShowStandardEosConfirm(true);
        setStandardEosError(null);
    };

    const handleConfirmStandardEosSend = async () => {
        setStandardEosError(null);
        const outcome = await handleStandardEosSend();
        if (outcome !== 'forbidden') {
            setShowStandardEosConfirm(false);
        }
    };

    const resetStandardEosForm = () => {
        setStandardEosTripEndFrom('');
        setStandardEosTripEndTo('');
        setStandardEosCompanyName('');
        setStandardEosPartnerSearch('');
        setStandardEosError(null);
        setStandardEosResult(null);
        setStandardEosFieldErrors({});
    };

    const resetReminderForm = () => {
        setReminderDateStart('');
        setReminderDateEnd('');
        setReminderCompanyName('');
        setReminderPartnerSearch('');
        setReminderEmailError(null);
        setReminderEmailResult(null);
    };

    const latestAllowedReminderDateEnd = useMemo(() => getLatestAllowedReminderDateEnd(), []);

    const handleReminderEmailSend = async () => {
        if (!reminderDateStart.trim() || !reminderDateEnd.trim()) {
            setReminderEmailError({ kind: 'generic', message: t('pages.adminTripsDaily.reminders.dateRequired') });
            return;
        }
        if (reminderDateStart.trim() > reminderDateEnd.trim()) {
            setReminderEmailError(buildLocalPeriodOrderError(reminderDateStart, reminderDateEnd));
            return;
        }
        if (
            isReminderDateAfterLatestAllowed(reminderDateEnd)
            || isReminderDateAfterLatestAllowed(reminderDateStart)
        ) {
            setReminderEmailError(buildLocalTripEndPeriodError(reminderDateEnd));
            return;
        }
        try {
            setReminderEmailLoading(true);
            setReminderEmailError(null);
            setReminderEmailResult(null);
            const params = {
                reminder_date_start: reminderDateStart.trim(),
                reminder_date_end: reminderDateEnd.trim(),
            };
            if (reminderCompanyName && reminderCompanyName.trim() !== '') {
                params.company_name = reminderCompanyName.trim();
            }
            const result = await sendTripsClientsBankDetailsRemindersEmail(params);
            setReminderEmailResult(result);
        } catch (error) {
            const parsed = parseBankDetailsRemindersError(error, {
                reminderDateStart,
                reminderDateEnd,
            });
            if (parsed.kind === 'generic' && !parsed.message) {
                parsed.message = t('pages.adminTripsDaily.reminders.emailError');
            }
            setReminderEmailError(parsed);
        } finally {
            setReminderEmailLoading(false);
        }
    };

    const resetStandardReminderForm = () => {
        setStandardReminderDateStart('');
        setStandardReminderDateEnd('');
        setStandardReminderCompanyName('');
        setStandardReminderPartnerSearch('');
        setStandardReminderEmailError(null);
        setStandardReminderEmailResult(null);
    };

    const handleStandardReminderEmailSend = async () => {
        if (!standardReminderDateStart.trim() || !standardReminderDateEnd.trim()) {
            setStandardReminderEmailError({
                kind: 'generic',
                message: t('pages.adminTripsDaily.reminders.dateRequired'),
            });
            return;
        }
        if (standardReminderDateStart.trim() > standardReminderDateEnd.trim()) {
            setStandardReminderEmailError(
                buildLocalPeriodOrderError(standardReminderDateStart, standardReminderDateEnd)
            );
            return;
        }
        if (
            isReminderDateAfterLatestAllowed(standardReminderDateEnd)
            || isReminderDateAfterLatestAllowed(standardReminderDateStart)
        ) {
            setStandardReminderEmailError(buildLocalTripEndPeriodError(standardReminderDateEnd));
            return;
        }
        try {
            setStandardReminderEmailLoading(true);
            setStandardReminderEmailError(null);
            setStandardReminderEmailResult(null);
            const params = {
                reminder_date_start: standardReminderDateStart.trim(),
                reminder_date_end: standardReminderDateEnd.trim(),
            };
            if (standardReminderCompanyName && standardReminderCompanyName.trim() !== '') {
                params.company_name = standardReminderCompanyName.trim();
            }
            const result = await sendStandardTripsClientsBankDetailsRemindersEmail(params);
            if (result?.success === false || result?.code === 'FORBIDDEN_COMPANY') {
                const parsed = parseBankDetailsRemindersError(result, {
                    reminderDateStart: standardReminderDateStart,
                    reminderDateEnd: standardReminderDateEnd,
                    companyName: standardReminderCompanyName,
                });
                if (parsed.kind === 'generic' && !parsed.message) {
                    parsed.message = t(`${STANDARD_REMINDER_PREFIX}.emailError`);
                }
                setStandardReminderEmailError(parsed);
                return;
            }
            setStandardReminderEmailResult(result);
        } catch (error) {
            const parsed = parseBankDetailsRemindersError(error, {
                reminderDateStart: standardReminderDateStart,
                reminderDateEnd: standardReminderDateEnd,
                companyName: standardReminderCompanyName,
            });
            if (parsed.kind === 'generic' && !parsed.message) {
                parsed.message = t(`${STANDARD_REMINDER_PREFIX}.emailError`);
            }
            setStandardReminderEmailError(parsed);
        } finally {
            setStandardReminderEmailLoading(false);
        }
    };

    const handleEndOfStayFormSubmit = (e) => {
        e.preventDefault();
        const needsSendConfirm = endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY
            || endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT;
        if (needsSendConfirm) {
            setEndOfStayFieldErrors({});
            if (
                endOfStayTripEndFrom
                && endOfStayTripEndTo
                && endOfStayTripEndFrom.trim() > endOfStayTripEndTo.trim()
            ) {
                setEndOfStayFieldErrors({
                    trip_end_date_to: t(`${endOfStayConfirmPrefix}.validation.dateOrder`),
                });
                return;
            }
            setShowTriggeredRecapConfirm(true);
            setEndOfStayError(null);
            return;
        }
        handleEndOfStaySend();
    };

    const handleConfirmTriggeredRecapSend = async () => {
        setEndOfStayError(null);
        const outcome = await handleEndOfStaySend();
        if (outcome !== 'forbidden') {
            setShowTriggeredRecapConfirm(false);
        }
    };

    const endOfStayActionLabel = (action) => {
        if (action === 'send_lac_des_graves_promise_summary_and_rib_request') {
            return 'Promesse + demande RIB';
        }
        if (action === 'send_lac_des_graves_NoTrigger_observation') {
            return 'Récap non déclenché (observation)';
        }
        if (action === 'promise_summary_and_rib_request') {
            return 'Promesse + demande RIB';
        }
        if (action === 'recap_guarantee_not_triggered') {
            return 'Récap garantie non déclenchée';
        }
        return action ?? '—';
    };

    const renderEndOfStayTriggeredOnlyResult = (result, { isPreview = false, onDismiss }) => {
        if (!result) return null;
        const details = Array.isArray(result.policy_claim_details) ? result.policy_claim_details : [];
        const isSuccess = result.status === 'success'
            || result.status === 'Success'
            || details.length > 0;
        return (
            <Alert
                variant={isPreview ? 'info' : (isSuccess ? 'success' : 'info')}
                className="mt-3"
                dismissible
                onClose={onDismiss}
            >
                {typeof result === 'object' && result !== null ? (
                    <>
                        {isSuccess && (
                            <p className="mb-2 d-flex align-items-center gap-2 fw-semibold text-success">
                                <FiCheckCircle aria-hidden />
                                {result.message || 'Récap email coordonnées bancaires : traitement terminé.'}
                            </p>
                        )}
                        {!isSuccess && result.message && (
                            <p className="mb-1"><strong>Message :</strong> {result.message}</p>
                        )}
                        {result.status && (
                            <p className="mb-1"><strong>Status :</strong> {result.status}</p>
                        )}
                        {result.timestamp && (
                            <p className={details.length > 0 ? 'mb-3' : 'mb-0'}><strong>Timestamp :</strong> {result.timestamp}</p>
                        )}
                        {details.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Code police</th>
                                            <th>Client</th>
                                            <th>Email</th>
                                            <th>N° réservation</th>
                                            <th>Code sinistre</th>
                                            <th>Statut</th>
                                            <th>Date sinistre</th>
                                            <th>Montant</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.flatMap((row, idx) => {
                                            const pi = row.policy_info || {};
                                            const ci = row.client_info || {};
                                            const ai = row.activity_info || {};
                                            const claims = Array.isArray(row.claims) ? row.claims : [];
                                            const clientName = [ci.first_name, ci.last_name].filter(Boolean).join(' ').trim() || '—';
                                            if (claims.length === 0) {
                                                return (
                                                    <tr key={`eos-detail-${idx}-nopolicy`}>
                                                        <td>{pi.policy_code ?? '—'}</td>
                                                        <td>{clientName}</td>
                                                        <td>{ci.email ?? '—'}</td>
                                                        <td>{ai.reservation_number ?? '—'}</td>
                                                        <td colSpan={4} className="text-muted small">Aucun sinistre sur cette police</td>
                                                    </tr>
                                                );
                                            }
                                            return claims.map((cl, j) => (
                                                <tr key={`eos-detail-${idx}-${cl.claim_code || j}`}>
                                                    <td>{pi.policy_code ?? '—'}</td>
                                                    <td>{clientName}</td>
                                                    <td>{ci.email ?? '—'}</td>
                                                    <td>{ai.reservation_number ?? '—'}</td>
                                                    <td><code className="small">{cl.claim_code ?? '—'}</code></td>
                                                    <td>{cl.claim_status ?? '—'}</td>
                                                    <td>{cl.date_of_claim ?? '—'}</td>
                                                    <td>{cl.amount_of_claim ?? '—'}</td>
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                ) : (
                    <span>{String(result)}</span>
                )}
            </Alert>
        );
    };

    const renderEndOfStayPoliciesResult = (result, { isPreview = false, onDismiss }) => {
        if (!result) return null;
        const policies = Array.isArray(result.policies) ? result.policies : [];
        const isSuccess = result.status === 'success'
            || result.status === 'Success'
            || policies.length > 0
            || (typeof result.total_policies === 'number' && result.total_policies >= 0);
        return (
            <Alert
                variant={isPreview ? 'info' : (isSuccess ? 'success' : 'info')}
                className="mt-3"
                dismissible
                onClose={onDismiss}
            >
                {typeof result === 'object' && result !== null ? (
                    <>
                        {!isPreview && isSuccess && (
                            <p className="mb-2 d-flex align-items-center gap-2 fw-semibold text-success">
                                <FiCheckCircle aria-hidden />
                                Envoi terminé
                                {typeof result.total_policies === 'number' ? ` — ${result.total_policies} police(s) traitée(s)` : ''}
                                .
                            </p>
                        )}
                        {isPreview && (
                            <p className="mb-2 fw-semibold">Aperçu (aucun e-mail envoyé)</p>
                        )}
                        {(result.trip_end_date_from || result.trip_end_date_to) && (
                            <p className="mb-1 small">
                                <strong>Plage :</strong>{' '}
                                {result.trip_end_date_from ?? '—'} → {result.trip_end_date_to ?? '—'}
                            </p>
                        )}
                        {result.message && (
                            <p className="mb-1"><strong>Message :</strong> {result.message}</p>
                        )}
                        {result.status && (
                            <p className="mb-1"><strong>Status :</strong> {result.status}</p>
                        )}
                        {result.timestamp && (
                            <p className={policies.length > 0 ? 'mb-3' : 'mb-0'}><strong>Timestamp :</strong> {result.timestamp}</p>
                        )}
                        {policies.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <Table striped bordered hover size="sm" className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Code police</th>
                                            <th>Policy ID</th>
                                            <th>Email client</th>
                                            <th>Action / e-mail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {policies.map((p, idx) => (
                                            <tr key={p.policy_id || `${p.policy_code}-${idx}`}>
                                                <td><code className="small">{p.policy_code ?? '—'}</code></td>
                                                <td><code className="small text-break">{p.policy_id ?? '—'}</code></td>
                                                <td>{p.client_email ?? '—'}</td>
                                                <td title={p.action || undefined}>
                                                    <span className="small">{endOfStayActionLabel(p.action)}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                        {policies.length === 0 && typeof result.total_policies === 'number' && result.total_policies === 0 && (
                            <p className="mb-0 small text-muted">Aucune police dans la plage pour cet envoi.</p>
                        )}
                    </>
                ) : (
                    <span>{String(result)}</span>
                )}
            </Alert>
        );
    };

    const renderEndOfStayResult = (result, { isPreview = false } = {}) => {
        if (!result) return null;
        const onDismiss = isPreview
            ? () => setEndOfStayPreviewResult(null)
            : () => setEndOfStayResult(null);

        if (!isPreview && isTriggeredRecapResponse(result)) {
            if (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY) {
                return (
                    <TriggeredRecapResult
                        result={result}
                        onDismiss={onDismiss}
                        recapKind="flower"
                    />
                );
            }
            if (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT) {
                return (
                    <TriggeredRecapResult
                        result={result}
                        onDismiss={onDismiss}
                        recapKind="endOfStay"
                    />
                );
            }
        }

        if (endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY) {
            return renderEndOfStayTriggeredOnlyResult(result, { isPreview, onDismiss });
        }
        return renderEndOfStayPoliciesResult(result, { isPreview, onDismiss });
    };

    const handleOpenEmailModal = () => {
        setEmailError(null);
        setShowEmailModal(true);
    };

    const handleOpenSmsModal = () => {
        setSmsError(null);
        setShowSmsModal(true);
    };

    const handleCloseEmailModal = () => {
        if (!emailLoading) {
            setShowEmailModal(false);
            setEmailError(null);
        }
    };

    const handleCloseSmsModal = () => {
        if (!smsLoading) {
            setShowSmsModal(false);
            setSmsError(null);
        }
    };

    const renderSendResultAlert = (result, type) => {
        if (!result || !isBulkSendSuccess(result)) return null;
        const isEmail = type === 'email';
        const data = result.data || {};
        const emailsSent = result.total_filtered_emails_sent ?? data.emails_sent;
        const smsSent = result.total_filtered_sms_sent ?? data.sms_sent;
        const totalPolicies = data.total_policies;

        return (
            <Alert
                variant="success"
                className="mt-3"
                dismissible
                onClose={() => (isEmail ? setEmailResult(null) : setSmsResult(null))}
            >
                <p className="mb-1">
                    <strong>{isEmail ? t('pages.adminTripsDaily.emailsSent') : t('pages.adminTripsDaily.smsSuccess')}</strong>
                </p>
                {result.message && (
                    <p className="mb-1"><strong>Message :</strong> {result.message}</p>
                )}
                {emailsSent !== undefined && (
                    <p className="mb-1"><strong>Total emails envoyés :</strong> {emailsSent}</p>
                )}
                {smsSent !== undefined && (
                    <p className="mb-1"><strong>Total SMS envoyés :</strong> {smsSent}</p>
                )}
                {totalPolicies !== undefined && (
                    <p className="mb-1"><strong>Polices traitées :</strong> {totalPolicies}</p>
                )}
                {result.count !== undefined && (
                    <p className="mb-0"><strong>Count :</strong> {result.count}</p>
                )}
            </Alert>
        );
    };


    return (
        <div className="dashboard-page admin-trips-daily-page">
            <Card>
                <Card.Body>
                    <header className="tn-page-header">
                        <h1 className="tn-page-title">
                            <FiBell size={24} aria-hidden />
                            {t('pages.adminTripsDaily.title')}
                        </h1>
                        <p className="tn-page-subtitle">{t('pages.adminTripsDaily.subtitle')}</p>
                    </header>

                    <Nav variant="tabs" className="tn-main-tabs" role="tablist" aria-label={t('pages.adminTripsDaily.title')}>
                        <Nav.Item>
                            <Nav.Link
                                active={mainGroup === MAIN_GROUPS.FORECASTS}
                                onClick={() => selectMainGroup(MAIN_GROUPS.FORECASTS)}
                                role="tab"
                                aria-selected={mainGroup === MAIN_GROUPS.FORECASTS}
                            >
                                {t('pages.adminTripsDaily.groups.forecasts')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                active={mainGroup === MAIN_GROUPS.OBSERVATIONS}
                                onClick={() => selectMainGroup(MAIN_GROUPS.OBSERVATIONS)}
                                role="tab"
                                aria-selected={mainGroup === MAIN_GROUPS.OBSERVATIONS}
                            >
                                {t('pages.adminTripsDaily.groups.observations')}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {mainGroup === MAIN_GROUPS.OBSERVATIONS ? (
                        <>
                            <div className="tn-subnav-wrap">
                                <Nav
                                    className="tn-subnav"
                                    role="tablist"
                                    aria-label={t('pages.adminTripsDaily.groups.observations')}
                                >
                                    {OBSERVATION_GROUP_LIST.map((groupKey) => {
                                        const isActive = observationGroup === groupKey;
                                        return (
                                            <Nav.Item key={groupKey}>
                                                <Nav.Link
                                                    active={isActive}
                                                    onClick={() => selectObservationSlot(groupKey, observationVariant)}
                                                    role="tab"
                                                    aria-selected={isActive}
                                                >
                                                    <span>{t(`pages.adminTripsDaily.forecastGroups.${groupKey}`)}</span>
                                                </Nav.Link>
                                            </Nav.Item>
                                        );
                                    })}
                                </Nav>
                            </div>

                            {!currentSlot.standalone && (
                                <Nav
                                    className="tn-segmented"
                                    role="tablist"
                                    aria-label={t('pages.adminTripsDaily.variants.label')}
                                >
                                    {[FORECAST_VARIANTS.STANDARD, FORECAST_VARIANTS.SPECIFIC].map((variantKey) => (
                                        <Nav.Item key={variantKey}>
                                            <Nav.Link
                                                active={observationVariant === variantKey}
                                                onClick={() => selectObservationSlot(observationGroup, variantKey)}
                                                role="tab"
                                                aria-selected={observationVariant === variantKey}
                                            >
                                                {t(`pages.adminTripsDaily.variants.${variantKey}`)}
                                            </Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                            )}
                        </>
                    ) : (
                    <>
                        <div className="tn-subnav-wrap">
                            <Nav
                                className="tn-subnav"
                                role="tablist"
                                aria-label={t('pages.adminTripsDaily.groups.forecasts')}
                            >
                                {FORECAST_GROUP_LIST.map((groupKey) => {
                                    const isActive = forecastGroup === groupKey;
                                    return (
                                        <Nav.Item key={groupKey}>
                                            <Nav.Link
                                                active={isActive}
                                                onClick={() => selectForecastSlot(groupKey, forecastVariant)}
                                                role="tab"
                                                aria-selected={isActive}
                                            >
                                                <span>{t(`pages.adminTripsDaily.forecastGroups.${groupKey}`)}</span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    );
                                })}
                            </Nav>
                        </div>

                        {!currentSlot.standalone && (
                        <Nav
                            className="tn-segmented"
                            role="tablist"
                            aria-label={t('pages.adminTripsDaily.variants.label')}
                        >
                            {[FORECAST_VARIANTS.STANDARD, FORECAST_VARIANTS.SPECIFIC].map((variantKey) => (
                                <Nav.Item key={variantKey}>
                                    <Nav.Link
                                        active={forecastVariant === variantKey}
                                        onClick={() => selectForecastSlot(forecastGroup, variantKey)}
                                        role="tab"
                                        aria-selected={forecastVariant === variantKey}
                                    >
                                        {t(`pages.adminTripsDaily.variants.${variantKey}`)}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        )}
                    </>
                    )}

                        {Array.isArray(currentSlot.tools) && currentSlot.tools.length > 0 && (
                            <Nav className="tn-tools-nav" role="tablist">
                                {currentSlot.tools.map((tool) => (
                                    <Nav.Item key={tool.key}>
                                        <Nav.Link
                                            active={activeTab === tool.key}
                                            onClick={() => setActiveTab(tool.key)}
                                            role="tab"
                                            aria-selected={activeTab === tool.key}
                                        >
                                            {t(tool.labelKey)}
                                        </Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        )}

                    <div className="tn-content-divider" aria-hidden="true" />

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || currentSlot.tab)}>
                        <Tab.Content>
                            {/* Onglet: Déclenchements du jour */}
                            <Tab.Pane eventKey="today-triggered">
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small mb-3">
                                            {t('pages.adminTripsDaily.partnerFilterDescription')}
                                        </p>
                                        <Form.Control
                                            type="text"
                                            value={partnerSearch}
                                            onChange={(e) => setPartnerSearch(e.target.value)}
                                            placeholder={t('pages.adminTripsDaily.partnerSearch')}
                                            disabled={forecastPartnersLoading || emailLoading || smsLoading}
                                            className="mb-2"
                                        />
                                        <div
                                            className="border rounded p-2 bg-white"
                                            style={{ maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id="daily-partner-all"
                                                label={t('pages.adminTripsDaily.allPartners')}
                                                checked={selectedPartnerNames.length === 0}
                                                disabled={forecastPartnersLoading || emailLoading || smsLoading}
                                                onChange={() => setSelectedPartnerNames([])}
                                                className="mb-2 fw-semibold"
                                            />
                                            {filteredPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                <div className="text-muted small px-1 py-2">Aucun partenaire trouvé.</div>
                                            ) : (
                                                filteredPartnerOptions.map((companyName, index) => (
                                                    <Form.Check
                                                        key={`daily-partner-${companyName}-${index}`}
                                                        type="checkbox"
                                                        id={`daily-partner-${companyName}-${index}`}
                                                        label={companyName}
                                                        checked={selectedPartnerNames.includes(companyName)}
                                                        disabled={forecastPartnersLoading || emailLoading || smsLoading}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedPartnerNames((prev) => [...prev, companyName]);
                                                            } else {
                                                                setSelectedPartnerNames((prev) => prev.filter((name) => name !== companyName));
                                                            }
                                                        }}
                                                        className="mb-1"
                                                    />
                                                ))
                                            )}
                                        </div>
                                        {selectedPartnerNames.length > 0 && (
                                            <div className="mt-2 d-flex flex-wrap gap-1">
                                                {selectedPartnerNames.map((name) => (
                                                    <Badge bg="secondary" key={`selected-daily-${name}`}>{name}</Badge>
                                                ))}
                                            </div>
                                        )}
                                        {forecastPartnersLoading && (
                                            <div className="text-muted small mt-1">{t('common.loading')}</div>
                                        )}
                                        {forecastPartnersError && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-2">{forecastPartnersError}</Alert>
                                        )}
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-end gap-2 mb-3 flex-wrap">
                                    <Button
                                        variant="primary"
                                        onClick={handleOpenEmailModal}
                                        disabled={forecastPartnersLoading || emailLoading || smsLoading}
                                        className="d-flex align-items-center"
                                    >
                                        <FiMail className="me-2" />
                                        {t('pages.adminTripsDaily.sendEmails')}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={handleOpenSmsModal}
                                        disabled={forecastPartnersLoading || emailLoading || smsLoading}
                                        className="d-flex align-items-center"
                                    >
                                        <FiMessageSquare className="me-2" />
                                        {t('pages.adminTripsDaily.sendSms')}
                                    </Button>
                                </div>

                                {emailError && (
                                    <Alert variant="danger" dismissible onClose={() => setEmailError(null)}>
                                        <strong>{t('pages.adminTripsDaily.error')}</strong> {emailError}
                                    </Alert>
                                )}
                                {renderSendResultAlert(emailResult, 'email')}
                                {renderSendResultAlert(smsResult, 'sms')}
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-forecast-trigger-email">
                                <StandardForecastTriggerEmailTab
                                    partnerOptions={partnerCompanyOptions}
                                    partnersLoading={forecastPartnersLoading}
                                    partnersError={forecastPartnersError}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="end-of-stay-recap">
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>Récap fin de séjour</h5>
                                        <p className="mb-2">
                                            Choisissez le <strong>type de récap</strong>. Pour le type
                                            « déclenchement et non déclenchement », sélectionnez <strong>Aloa</strong> ou{' '}
                                            <strong>Lac des Graves</strong>.
                                        </p>
                                        <ul className="mb-2">
                                            <li>
                                                <strong>Récap fin de séjour déclenchement uniquement</strong> — filtre partenaire optionnel (
                                                <code>company_name</code>).
                                            </li>
                                            <li>
                                                <strong>Récap déclenchement et non déclenchement</strong> — choix du flux Aloa ou Lac des Graves.
                                            </li>
                                        </ul>
                                        <div className="mt-1">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="d-inline-flex align-items-center gap-1"
                                                onClick={() => setShowEndOfStayEndpoint((prev) => !prev)}
                                                aria-expanded={showEndOfStayEndpoint}
                                                aria-controls="end-of-stay-endpoint-details"
                                            >
                                                {showEndOfStayEndpoint ? (
                                                    <>
                                                        <FiChevronUp aria-hidden />
                                                        Voir moins
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiChevronDown aria-hidden />
                                                        Voir plus
                                                    </>
                                                )}
                                            </Button>
                                            <Collapse in={showEndOfStayEndpoint}>
                                                <div id="end-of-stay-endpoint-details" className="mt-2">
                                                    <p className="mb-0 small text-muted">
                                                        <strong>Endpoint sélectionné :</strong>{' '}
                                                        <code className="text-break">{endOfStayEndpointLabel}</code>
                                                    </p>
                                                </div>
                                            </Collapse>
                                        </div>
                                    </Card.Body>
                                </Card>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small mb-3">
                                            {t('pages.adminTripsDaily.standardForecastTriggerEmail.filterDescription')}
                                        </p>
                                        <Form.Control
                                            type="text"
                                            value={endOfStayPartnerSearch}
                                            onChange={(e) => setEndOfStayPartnerSearch(e.target.value)}
                                            placeholder={t('pages.adminTripsDaily.partnerSearch')}
                                            disabled={forecastPartnersLoading || endOfStayLoading || endOfStayPreviewLoading}
                                            className="mb-2"
                                        />
                                        <div
                                            className="border rounded p-2 bg-white"
                                            style={{ maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id="end-of-stay-partner-all"
                                                label={t('pages.adminTripsDaily.allPartners')}
                                                checked={!endOfStayCompanyName.trim()}
                                                disabled={forecastPartnersLoading || endOfStayLoading || endOfStayPreviewLoading}
                                                onChange={() => {
                                                    setEndOfStayCompanyName('');
                                                    setEndOfStayFieldErrors((prev) => {
                                                        const next = { ...prev };
                                                        delete next.company_name;
                                                        return next;
                                                    });
                                                }}
                                                className="mb-2 fw-semibold"
                                            />
                                            {filteredEndOfStayPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                <div className="text-muted small px-1 py-2">
                                                    {t('pages.adminTripsDaily.standardForecastTriggerEmail.noPartners')}
                                                </div>
                                            ) : (
                                                filteredEndOfStayPartnerOptions.map((companyName, index) => (
                                                    <Form.Check
                                                        key={`end-of-stay-partner-${companyName}-${index}`}
                                                        type="checkbox"
                                                        id={`end-of-stay-partner-${companyName}-${index}`}
                                                        label={companyName}
                                                        checked={endOfStayCompanyName === companyName}
                                                        disabled={forecastPartnersLoading || endOfStayLoading || endOfStayPreviewLoading}
                                                        onChange={(e) => {
                                                            const nextValue = e.target.checked ? companyName : '';
                                                            setEndOfStayCompanyName(nextValue);
                                                            setEndOfStayFieldErrors((prev) => {
                                                                const next = { ...prev };
                                                                delete next.company_name;
                                                                return next;
                                                            });
                                                        }}
                                                        className="mb-1"
                                                    />
                                                ))
                                            )}
                                        </div>
                                        {endOfStayCompanyName.trim() ? (
                                            <div className="mt-2 d-flex flex-wrap gap-1">
                                                <Badge bg="secondary">{endOfStayCompanyName}</Badge>
                                            </div>
                                        ) : null}
                                        {forecastPartnersLoading && (
                                            <div className="text-muted small mt-1">{t('common.loading')}</div>
                                        )}
                                        {forecastPartnersError && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-2">
                                                {forecastPartnersError}
                                            </Alert>
                                        )}
                                        {endOfStayFieldErrors.company_name && (
                                            <div className="text-danger small mt-2">
                                                {endOfStayFieldErrors.company_name}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Paramètres</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={handleEndOfStayFormSubmit}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Type de récap</Form.Label>
                                                <Form.Select
                                                    value={endOfStayRecapType}
                                                    onChange={(e) => {
                                                        setEndOfStayRecapType(e.target.value);
                                                        setEndOfStayError(null);
                                                        setEndOfStayPreviewError(null);
                                                        setEndOfStayResult(null);
                                                        setEndOfStayPreviewResult(null);
                                                    }}
                                                    disabled={endOfStayLoading || endOfStayPreviewLoading}
                                                >
                                                    <option value={END_OF_STAY_RECAP_TYPE.TRIGGERED_ONLY}>
                                                        Récap fin de séjour déclenchement uniquement
                                                    </option>
                                                    <option value={END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT}>
                                                        Récap déclenchement et non déclenchement
                                                    </option>
                                                </Form.Select>
                                            </Form.Group>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Date fin voyage (de)</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={endOfStayTripEndFrom}
                                                            onChange={(e) => {
                                                                setEndOfStayTripEndFrom(e.target.value);
                                                                setEndOfStayFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.trip_end_date_from;
                                                                    delete next.trip_end_date_to;
                                                                    return next;
                                                                });
                                                            }}
                                                            isInvalid={!!endOfStayFieldErrors.trip_end_date_from}
                                                            disabled={endOfStayLoading || endOfStayPreviewLoading}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {endOfStayFieldErrors.trip_end_date_from}
                                                        </Form.Control.Feedback>
                                                        <Form.Text className="text-muted">
                                                            Format YYYY-MM-DD. Vide = défaut backend (souvent hier).
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Date fin voyage (à)</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={endOfStayTripEndTo}
                                                            onChange={(e) => {
                                                                setEndOfStayTripEndTo(e.target.value);
                                                                setEndOfStayFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.trip_end_date_to;
                                                                    return next;
                                                                });
                                                            }}
                                                            isInvalid={!!endOfStayFieldErrors.trip_end_date_to}
                                                            disabled={endOfStayLoading || endOfStayPreviewLoading}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {endOfStayFieldErrors.trip_end_date_to}
                                                        </Form.Control.Feedback>
                                                        <Form.Text className="text-muted">
                                                            Format YYYY-MM-DD. Vide = défaut backend (souvent aujourd&apos;hui).
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            {endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT && (
                                                <Form.Group className="mb-4">
                                                    <Form.Label>Flux</Form.Label>
                                                    <Form.Select
                                                        value={endOfStayProvider}
                                                        onChange={(e) => {
                                                            setEndOfStayProvider(e.target.value);
                                                            setEndOfStayError(null);
                                                            setEndOfStayPreviewError(null);
                                                            setEndOfStayResult(null);
                                                            setEndOfStayPreviewResult(null);
                                                        }}
                                                        disabled={endOfStayLoading || endOfStayPreviewLoading}
                                                    >
                                                        <option value={END_OF_STAY_PROVIDER.ALOA}>Aloa</option>
                                                        <option value={END_OF_STAY_PROVIDER.LAC_DES_GRAVES}>Lac des Graves</option>
                                                    </Form.Select>
                                                    <Form.Text className="text-muted">
                                                        Détermine l&apos;endpoint utilisé pour l&apos;envoi et l&apos;aperçu.
                                                    </Form.Text>
                                                </Form.Group>
                                            )}
                                            <div className="d-flex flex-wrap justify-content-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={resetEndOfStayForm}
                                                    disabled={endOfStayLoading || endOfStayPreviewLoading}
                                                >
                                                    Réinitialiser
                                                </Button>
                                                {endOfStaySupportsPreview && (
                                                    <Button
                                                        type="button"
                                                        variant="outline-primary"
                                                        onClick={handleEndOfStayPreview}
                                                        disabled={endOfStayLoading || endOfStayPreviewLoading || forecastPartnersLoading}
                                                    >
                                                        {endOfStayPreviewLoading ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" animation="border" />
                                                                Aperçu...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiEye className="me-2" />
                                                                Aperçu (sans envoi)
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={endOfStayLoading || endOfStayPreviewLoading || forecastPartnersLoading}
                                                >
                                                    {endOfStayLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            Envoi en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiMail className="me-2" />
                                                            Envoyer le récap
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {endOfStayPreviewError && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setEndOfStayPreviewError(null)}>
                                        <strong>Erreur aperçu :</strong> {endOfStayPreviewError}
                                    </Alert>
                                )}
                                {endOfStayError && !isTriggeredRecapResponse(endOfStayResult) && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setEndOfStayError(null)}>
                                        <strong>Erreur envoi :</strong> {endOfStayError}
                                    </Alert>
                                )}
                                {renderEndOfStayResult(endOfStayPreviewResult, { isPreview: true })}
                                {renderEndOfStayResult(endOfStayResult, { isPreview: false })}
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-end-of-stay-recap">
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>{t(`${STANDARD_EOS_PREFIX}.title`)}</h5>
                                        <p className="mb-2">{t(`${STANDARD_EOS_PREFIX}.description`)}</p>
                                        <ul className="mb-2">
                                            <li>{t(`${STANDARD_EOS_PREFIX}.bulletNotTriggered`)}</li>
                                            <li>{t(`${STANDARD_EOS_PREFIX}.bulletTriggered`)}</li>
                                        </ul>
                                        <p className="text-muted mb-0 small">
                                            <strong>{t(`${STANDARD_EOS_PREFIX}.endpointLabel`)} :</strong>{' '}
                                            <code className="text-break">{t(`${STANDARD_EOS_PREFIX}.endpointValue`)}</code>
                                        </p>
                                        <p className="text-muted mb-0 small mt-2">
                                            {t(`${STANDARD_EOS_PREFIX}.roleNote`)}
                                        </p>
                                    </Card.Body>
                                </Card>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small mb-3">
                                            {t('pages.adminTripsDaily.standardForecastTriggerEmail.filterDescription')}
                                        </p>
                                        <Form.Control
                                            type="text"
                                            value={standardEosPartnerSearch}
                                            onChange={(e) => setStandardEosPartnerSearch(e.target.value)}
                                            placeholder={t('pages.adminTripsDaily.partnerSearch')}
                                            disabled={forecastPartnersLoading || standardEosLoading}
                                            className="mb-2"
                                        />
                                        <div
                                            className="border rounded p-2 bg-white"
                                            style={{ maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id="standard-eos-partner-all"
                                                label={t('pages.adminTripsDaily.allPartners')}
                                                checked={!standardEosCompanyName.trim()}
                                                disabled={forecastPartnersLoading || standardEosLoading}
                                                onChange={() => {
                                                    setStandardEosCompanyName('');
                                                    setStandardEosFieldErrors((prev) => {
                                                        const next = { ...prev };
                                                        delete next.company_name;
                                                        return next;
                                                    });
                                                }}
                                                className="mb-2 fw-semibold"
                                            />
                                            {filteredStandardEosPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                <div className="text-muted small px-1 py-2">
                                                    {t('pages.adminTripsDaily.standardForecastTriggerEmail.noPartners')}
                                                </div>
                                            ) : (
                                                filteredStandardEosPartnerOptions.map((companyName, index) => (
                                                    <Form.Check
                                                        key={`standard-eos-partner-${companyName}-${index}`}
                                                        type="checkbox"
                                                        id={`standard-eos-partner-${companyName}-${index}`}
                                                        label={companyName}
                                                        checked={standardEosCompanyName === companyName}
                                                        disabled={forecastPartnersLoading || standardEosLoading}
                                                        onChange={(e) => {
                                                            const nextValue = e.target.checked ? companyName : '';
                                                            setStandardEosCompanyName(nextValue);
                                                            setStandardEosFieldErrors((prev) => {
                                                                const next = { ...prev };
                                                                delete next.company_name;
                                                                return next;
                                                            });
                                                        }}
                                                        className="mb-1"
                                                    />
                                                ))
                                            )}
                                        </div>
                                        {standardEosCompanyName.trim() ? (
                                            <div className="mt-2 d-flex flex-wrap gap-1">
                                                <Badge bg="secondary">{standardEosCompanyName}</Badge>
                                            </div>
                                        ) : null}
                                        {forecastPartnersLoading && (
                                            <div className="text-muted small mt-1">{t('common.loading')}</div>
                                        )}
                                        {forecastPartnersError && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-2">
                                                {forecastPartnersError}
                                            </Alert>
                                        )}
                                        {standardEosFieldErrors.company_name && (
                                            <div className="text-danger small mt-2">
                                                {standardEosFieldErrors.company_name}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">{t(`${STANDARD_EOS_PREFIX}.paramsTitle`)}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={handleStandardEosFormSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{t(`${STANDARD_EOS_PREFIX}.filterDateFrom`)}</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={standardEosTripEndFrom}
                                                            onChange={(e) => {
                                                                setStandardEosTripEndFrom(e.target.value);
                                                                setStandardEosFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.trip_end_date_from;
                                                                    delete next.trip_end_date_to;
                                                                    return next;
                                                                });
                                                            }}
                                                            isInvalid={!!standardEosFieldErrors.trip_end_date_from}
                                                            disabled={standardEosLoading}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {standardEosFieldErrors.trip_end_date_from}
                                                        </Form.Control.Feedback>
                                                        <Form.Text className="text-muted">
                                                            {t(`${STANDARD_EOS_PREFIX}.dateFromHint`)}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>{t(`${STANDARD_EOS_PREFIX}.filterDateTo`)}</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={standardEosTripEndTo}
                                                            onChange={(e) => {
                                                                setStandardEosTripEndTo(e.target.value);
                                                                setStandardEosFieldErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next.trip_end_date_to;
                                                                    return next;
                                                                });
                                                            }}
                                                            isInvalid={!!standardEosFieldErrors.trip_end_date_to}
                                                            disabled={standardEosLoading}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {standardEosFieldErrors.trip_end_date_to}
                                                        </Form.Control.Feedback>
                                                        <Form.Text className="text-muted">
                                                            {t(`${STANDARD_EOS_PREFIX}.dateToHint`)}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <div className="d-flex flex-wrap justify-content-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={resetStandardEosForm}
                                                    disabled={standardEosLoading}
                                                >
                                                    {t('common.reset')}
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={standardEosLoading || forecastPartnersLoading}
                                                >
                                                    {standardEosLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            {t(`${STANDARD_EOS_PREFIX}.sending`)}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiMail className="me-2" />
                                                            {t(`${STANDARD_EOS_PREFIX}.sendButton`)}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {standardEosError && !isTriggeredRecapResponse(standardEosResult) && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setStandardEosError(null)}>
                                        <strong>{t('pages.adminTripsDaily.error')}</strong> {standardEosError}
                                    </Alert>
                                )}
                                {isTriggeredRecapResponse(standardEosResult) && (
                                    <TriggeredRecapResult
                                        result={standardEosResult}
                                        onDismiss={() => setStandardEosResult(null)}
                                        recapKind="standard"
                                    />
                                )}
                                {standardEosResult && !isTriggeredRecapResponse(standardEosResult) && (
                                    renderEndOfStayPoliciesResult(standardEosResult, {
                                        isPreview: false,
                                        onDismiss: () => setStandardEosResult(null),
                                    })
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="bank-details-reminders">
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>{t('pages.adminTripsDaily.reminders.title')}</h5>
                                        <p className="mb-2">{t('pages.adminTripsDaily.reminders.description')}</p>
                                        <p className="text-muted mb-0 small">
                                            <strong>Endpoint :</strong>{' '}
                                            <code className="text-break">POST /api/trips/clients/bank-details/send-reminders-email</code>
                                        </p>
                                    </Card.Body>
                                </Card>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small mb-3">
                                            {t('pages.adminTripsDaily.standardForecastTriggerEmail.filterDescription')}
                                        </p>
                                        <Form.Control
                                            type="text"
                                            value={reminderPartnerSearch}
                                            onChange={(e) => setReminderPartnerSearch(e.target.value)}
                                            placeholder={t('pages.adminTripsDaily.partnerSearch')}
                                            disabled={forecastPartnersLoading || reminderEmailLoading}
                                            className="mb-2"
                                        />
                                        <div
                                            className="border rounded p-2 bg-white"
                                            style={{ maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id="reminder-partner-all"
                                                label={t('pages.adminTripsDaily.allPartners')}
                                                checked={!reminderCompanyName.trim()}
                                                disabled={forecastPartnersLoading || reminderEmailLoading}
                                                onChange={() => setReminderCompanyName('')}
                                                className="mb-2 fw-semibold"
                                            />
                                            {filteredReminderPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                <div className="text-muted small px-1 py-2">
                                                    {t('pages.adminTripsDaily.standardForecastTriggerEmail.noPartners')}
                                                </div>
                                            ) : (
                                                filteredReminderPartnerOptions.map((companyName, index) => (
                                                    <Form.Check
                                                        key={`reminder-partner-${companyName}-${index}`}
                                                        type="checkbox"
                                                        id={`reminder-partner-${companyName}-${index}`}
                                                        label={companyName}
                                                        checked={reminderCompanyName === companyName}
                                                        disabled={forecastPartnersLoading || reminderEmailLoading}
                                                        onChange={(e) => {
                                                            setReminderCompanyName(e.target.checked ? companyName : '');
                                                        }}
                                                        className="mb-1"
                                                    />
                                                ))
                                            )}
                                        </div>
                                        {reminderCompanyName.trim() ? (
                                            <div className="mt-2 d-flex flex-wrap gap-1">
                                                <Badge bg="secondary">{reminderCompanyName}</Badge>
                                            </div>
                                        ) : null}
                                        {forecastPartnersLoading && (
                                            <div className="text-muted small mt-1">{t('common.loading')}</div>
                                        )}
                                        {forecastPartnersError && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-2">
                                                {forecastPartnersError}
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">{t('pages.adminTripsDaily.reminders.paramsTitle')}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleReminderEmailSend();
                                            }}
                                        >
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('pages.adminTripsDaily.reminders.dateStart')}{' '}
                                                            <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={reminderDateStart}
                                                            max={latestAllowedReminderDateEnd}
                                                            onChange={(e) => {
                                                                setReminderDateStart(e.target.value);
                                                                setReminderEmailError(null);
                                                            }}
                                                            disabled={reminderEmailLoading}
                                                            required
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t('pages.adminTripsDaily.reminders.dateStartHint', {
                                                                maxDate: formatDateDdMmYyyy(latestAllowedReminderDateEnd),
                                                            })}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('pages.adminTripsDaily.reminders.dateEnd')}{' '}
                                                            <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={reminderDateEnd}
                                                            max={latestAllowedReminderDateEnd}
                                                            onChange={(e) => {
                                                                setReminderDateEnd(e.target.value);
                                                                setReminderEmailError(null);
                                                            }}
                                                            disabled={reminderEmailLoading}
                                                            required
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t('pages.adminTripsDaily.reminders.dateEndHint', {
                                                                maxDate: formatDateDdMmYyyy(latestAllowedReminderDateEnd),
                                                                todayDate: formatDateDdMmYyyy(getParisTodayIso()),
                                                            })}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <div className="d-flex flex-wrap justify-content-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={resetReminderForm}
                                                    disabled={reminderEmailLoading}
                                                >
                                                    {t('common.reset')}
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={
                                                        reminderEmailLoading
                                                        || forecastPartnersLoading
                                                        || !reminderDateStart
                                                        || !reminderDateEnd
                                                    }
                                                >
                                                    {reminderEmailLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            {t('pages.adminTripsDaily.reminders.sending')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiMail className="me-2" />
                                                            {t('pages.adminTripsDaily.reminders.sendEmail')}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {reminderEmailError && (
                                    <BankDetailsRemindersErrorAlert
                                        error={reminderEmailError}
                                        onClose={() => setReminderEmailError(null)}
                                    />
                                )}
                                {reminderEmailResult && (
                                    <BankDetailsRemindersResult
                                        result={reminderEmailResult}
                                        onDismiss={() => setReminderEmailResult(null)}
                                    />
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-bank-details-reminders">
                                <Card className="mb-4">
                                    <Card.Body>
                                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                            <h5 className="mb-0">{t(`${STANDARD_REMINDER_PREFIX}.title`)}</h5>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                size="sm"
                                                className="d-inline-flex align-items-center gap-1"
                                                onClick={() => setShowStandardReminderDetails((prev) => !prev)}
                                                aria-expanded={showStandardReminderDetails}
                                                aria-controls="standard-rib-reminder-details"
                                            >
                                                {showStandardReminderDetails ? (
                                                    <>
                                                        <FiChevronUp aria-hidden />
                                                        {t(`${STANDARD_REMINDER_PREFIX}.hideDetails`)}
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiEye aria-hidden />
                                                        {t(`${STANDARD_REMINDER_PREFIX}.showDetails`)}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <Collapse in={showStandardReminderDetails}>
                                            <div id="standard-rib-reminder-details" className="mt-3">
                                                <p className="mb-2">{t(`${STANDARD_REMINDER_PREFIX}.description`)}</p>
                                                <ul className="small text-muted mb-2">
                                                    <li>{t(`${STANDARD_REMINDER_PREFIX}.bulletValidated`)}</li>
                                                    <li>{t(`${STANDARD_REMINDER_PREFIX}.bulletDelay`)}</li>
                                                    <li>{t(`${STANDARD_REMINDER_PREFIX}.bulletScope`)}</li>
                                                </ul>
                                                <p className="text-muted mb-0 small">
                                                    <strong>{t(`${STANDARD_REMINDER_PREFIX}.endpointLabel`)} :</strong>{' '}
                                                    <code className="text-break">{t(`${STANDARD_REMINDER_PREFIX}.endpointValue`)}</code>
                                                </p>
                                                <p className="text-muted mb-0 small mt-2">
                                                    {t(`${STANDARD_REMINDER_PREFIX}.roleNote`)}
                                                </p>
                                            </div>
                                        </Collapse>
                                    </Card.Body>
                                </Card>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">{t('pages.adminTripsDaily.partnerFilterTitle')}</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-muted small mb-3">
                                            {t('pages.adminTripsDaily.standardForecastTriggerEmail.filterDescription')}
                                        </p>
                                        <Form.Control
                                            type="text"
                                            value={standardReminderPartnerSearch}
                                            onChange={(e) => setStandardReminderPartnerSearch(e.target.value)}
                                            placeholder={t('pages.adminTripsDaily.partnerSearch')}
                                            disabled={forecastPartnersLoading || standardReminderEmailLoading}
                                            className="mb-2"
                                        />
                                        <div
                                            className="border rounded p-2 bg-white"
                                            style={{ maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                id="standard-reminder-partner-all"
                                                label={t('pages.adminTripsDaily.allPartners')}
                                                checked={!standardReminderCompanyName.trim()}
                                                disabled={forecastPartnersLoading || standardReminderEmailLoading}
                                                onChange={() => setStandardReminderCompanyName('')}
                                                className="mb-2 fw-semibold"
                                            />
                                            {filteredStandardReminderPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                <div className="text-muted small px-1 py-2">
                                                    {t('pages.adminTripsDaily.standardForecastTriggerEmail.noPartners')}
                                                </div>
                                            ) : (
                                                filteredStandardReminderPartnerOptions.map((companyName, index) => (
                                                    <Form.Check
                                                        key={`standard-reminder-partner-${companyName}-${index}`}
                                                        type="checkbox"
                                                        id={`standard-reminder-partner-${companyName}-${index}`}
                                                        label={companyName}
                                                        checked={standardReminderCompanyName === companyName}
                                                        disabled={forecastPartnersLoading || standardReminderEmailLoading}
                                                        onChange={(e) => {
                                                            setStandardReminderCompanyName(e.target.checked ? companyName : '');
                                                        }}
                                                        className="mb-1"
                                                    />
                                                ))
                                            )}
                                        </div>
                                        {standardReminderCompanyName.trim() ? (
                                            <div className="mt-2 d-flex flex-wrap gap-1">
                                                <Badge bg="secondary">{standardReminderCompanyName}</Badge>
                                            </div>
                                        ) : null}
                                        {forecastPartnersLoading && (
                                            <div className="text-muted small mt-1">{t('common.loading')}</div>
                                        )}
                                        {forecastPartnersError && (
                                            <Alert variant="danger" className="mt-2 mb-0 py-2">
                                                {forecastPartnersError}
                                            </Alert>
                                        )}
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">{t(`${STANDARD_REMINDER_PREFIX}.paramsTitle`)}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleStandardReminderEmailSend();
                                            }}
                                        >
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('pages.adminTripsDaily.reminders.dateStart')}{' '}
                                                            <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={standardReminderDateStart}
                                                            max={latestAllowedReminderDateEnd}
                                                            onChange={(e) => {
                                                                setStandardReminderDateStart(e.target.value);
                                                                setStandardReminderEmailError(null);
                                                            }}
                                                            disabled={standardReminderEmailLoading}
                                                            required
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t('pages.adminTripsDaily.reminders.dateStartHint', {
                                                                maxDate: formatDateDdMmYyyy(latestAllowedReminderDateEnd),
                                                            })}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            {t('pages.adminTripsDaily.reminders.dateEnd')}{' '}
                                                            <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={standardReminderDateEnd}
                                                            max={latestAllowedReminderDateEnd}
                                                            onChange={(e) => {
                                                                setStandardReminderDateEnd(e.target.value);
                                                                setStandardReminderEmailError(null);
                                                            }}
                                                            disabled={standardReminderEmailLoading}
                                                            required
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t('pages.adminTripsDaily.reminders.dateEndHint', {
                                                                maxDate: formatDateDdMmYyyy(latestAllowedReminderDateEnd),
                                                                todayDate: formatDateDdMmYyyy(getParisTodayIso()),
                                                            })}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <div className="d-flex flex-wrap justify-content-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={resetStandardReminderForm}
                                                    disabled={standardReminderEmailLoading}
                                                >
                                                    {t('common.reset')}
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={
                                                        standardReminderEmailLoading
                                                        || forecastPartnersLoading
                                                        || !standardReminderDateStart
                                                        || !standardReminderDateEnd
                                                    }
                                                >
                                                    {standardReminderEmailLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            {t(`${STANDARD_REMINDER_PREFIX}.sending`)}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiMail className="me-2" />
                                                            {t(`${STANDARD_REMINDER_PREFIX}.sendEmail`)}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {standardReminderEmailError && (
                                    <BankDetailsRemindersErrorAlert
                                        error={standardReminderEmailError}
                                        onClose={() => setStandardReminderEmailError(null)}
                                    />
                                )}
                                {standardReminderEmailResult && (
                                    <BankDetailsRemindersResult
                                        result={standardReminderEmailResult}
                                        onDismiss={() => setStandardReminderEmailResult(null)}
                                    />
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="sms-monitor">
                                <SmsMonitorTab isActive={activeTab === 'sms-monitor'} />
                            </Tab.Pane>

                            <Tab.Pane eventKey="email-monitor">
                                <EmailMonitorTab isActive={activeTab === 'email-monitor'} />
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-forecast-not-triggered-email">
                                <StandardForecastNotTriggeredEmailTab
                                    partnerOptions={partnerCompanyOptions}
                                    partnersLoading={forecastPartnersLoading}
                                    partnersError={forecastPartnersError}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="not-trigger-specific">
                                <div className="tn-slot-placeholder">
                                    <Badge bg="secondary" className="tn-status-badge mb-3">
                                        {t('pages.adminTripsDaily.status.soon')}
                                    </Badge>
                                    <h5>{t('pages.adminTripsDaily.forecastGroups.not-trigger')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('pages.adminTripsDaily.placeholders.notTriggerSpecific')}
                                    </p>
                                </div>
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-observation-trigger-email">
                                <StandardObservationTriggerEmailTab
                                    partnerOptions={partnerCompanyOptions}
                                    partnersLoading={forecastPartnersLoading}
                                    partnersError={forecastPartnersError}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-observation-not-triggered-email">
                                <StandardObservationNotTriggeredEmailTab
                                    partnerOptions={partnerCompanyOptions}
                                    partnersLoading={forecastPartnersLoading}
                                    partnersError={forecastPartnersError}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="observation-trigger-specific">
                                <div className="tn-slot-placeholder">
                                    <Badge bg="secondary" className="tn-status-badge mb-3">
                                        {t('pages.adminTripsDaily.status.soon')}
                                    </Badge>
                                    <h5>{t('pages.adminTripsDaily.forecastGroups.trigger')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('pages.adminTripsDaily.placeholders.observationTriggerSpecific')}
                                    </p>
                                </div>
                            </Tab.Pane>

                            <Tab.Pane eventKey="observation-not-trigger-specific">
                                <div className="tn-slot-placeholder">
                                    <Badge bg="secondary" className="tn-status-badge mb-3">
                                        {t('pages.adminTripsDaily.status.soon')}
                                    </Badge>
                                    <h5>{t('pages.adminTripsDaily.forecastGroups.not-trigger')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('pages.adminTripsDaily.placeholders.observationNotTriggerSpecific')}
                                    </p>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>

                    <Modal
                        show={showTriggeredRecapConfirm}
                        onHide={() => {
                            if (!endOfStayLoading) {
                                setShowTriggeredRecapConfirm(false);
                                setEndOfStayError(null);
                            }
                        }}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiMail className="me-2" />
                                {t(`${endOfStayConfirmPrefix}.confirmTitle`)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {endOfStayError && (
                                <Alert
                                    variant="danger"
                                    className="mb-3"
                                    dismissible
                                    onClose={() => setEndOfStayError(null)}
                                >
                                    <div className="d-flex align-items-start gap-2">
                                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                                        <div>
                                            <strong className="d-block mb-1">
                                                {t('pages.adminTripsDaily.errors.forbiddenTitle')}
                                            </strong>
                                            <span>{endOfStayError}</span>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            <p className="mb-2">{t(`${endOfStayConfirmPrefix}.confirmMessage`)}</p>
                            {endOfStayRecapType === END_OF_STAY_RECAP_TYPE.TRIGGERED_AND_NOT && (
                                <p className="mb-2 small">
                                    <strong>{t('pages.adminTripsDaily.endOfStayRecap.providerLabel')} :</strong>{' '}
                                    {endOfStayProvider === END_OF_STAY_PROVIDER.LAC_DES_GRAVES
                                        ? t('pages.adminTripsDaily.endOfStayRecap.providerLdg')
                                        : t('pages.adminTripsDaily.endOfStayRecap.providerAloa')}
                                </p>
                            )}
                            <ul className="mb-2">
                                <li>
                                    {t(`${endOfStayConfirmPrefix}.filterDateFrom`)} :{' '}
                                    <strong>{endOfStayTripEndFrom ? formatDateDdMmYyyy(endOfStayTripEndFrom) : t(`${endOfStayConfirmPrefix}.defaultDateHint`)}</strong>
                                </li>
                                <li>
                                    {t(`${endOfStayConfirmPrefix}.filterDateTo`)} :{' '}
                                    <strong>{endOfStayTripEndTo ? formatDateDdMmYyyy(endOfStayTripEndTo) : t(`${endOfStayConfirmPrefix}.defaultDateHint`)}</strong>
                                </li>
                                <li>
                                    {t(`${endOfStayConfirmPrefix}.filterCompany`)} :{' '}
                                    <strong>{endOfStayCompanyName?.trim() || t(`${endOfStayConfirmPrefix}.allPartners`)}</strong>
                                </li>
                            </ul>
                            <p className="text-muted small mb-0">{t(`${endOfStayConfirmPrefix}.confirmWarning`)}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowTriggeredRecapConfirm(false);
                                    setEndOfStayError(null);
                                }}
                                disabled={endOfStayLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleConfirmTriggeredRecapSend} disabled={endOfStayLoading}>
                                {endOfStayLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t(`${endOfStayConfirmPrefix}.sending`)}
                                    </>
                                ) : (
                                    t(`${endOfStayConfirmPrefix}.confirmSend`)
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal
                        show={showStandardEosConfirm}
                        onHide={() => {
                            if (!standardEosLoading) {
                                setShowStandardEosConfirm(false);
                                setStandardEosError(null);
                            }
                        }}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>{t(`${STANDARD_EOS_PREFIX}.confirmTitle`)}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {standardEosError && (
                                <Alert
                                    variant="danger"
                                    className="mb-3"
                                    dismissible
                                    onClose={() => setStandardEosError(null)}
                                >
                                    <div className="d-flex align-items-start gap-2">
                                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                                        <div>
                                            <strong className="d-block mb-1">
                                                {t('pages.adminTripsDaily.errors.forbiddenTitle')}
                                            </strong>
                                            <span>{standardEosError}</span>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            <p className="mb-2">{t(`${STANDARD_EOS_PREFIX}.confirmMessage`)}</p>
                            <ul className="small mb-3">
                                <li>
                                    {t(`${STANDARD_EOS_PREFIX}.filterDateFrom`)} :{' '}
                                    <strong>
                                        {standardEosTripEndFrom
                                            ? formatDateDdMmYyyy(standardEosTripEndFrom)
                                            : t(`${STANDARD_EOS_PREFIX}.defaultDateHint`)}
                                    </strong>
                                </li>
                                <li>
                                    {t(`${STANDARD_EOS_PREFIX}.filterDateTo`)} :{' '}
                                    <strong>
                                        {standardEosTripEndTo
                                            ? formatDateDdMmYyyy(standardEosTripEndTo)
                                            : t(`${STANDARD_EOS_PREFIX}.defaultDateHint`)}
                                    </strong>
                                </li>
                                <li>
                                    {t(`${STANDARD_EOS_PREFIX}.filterCompany`)} :{' '}
                                    <strong>
                                        {standardEosCompanyName?.trim() || t(`${STANDARD_EOS_PREFIX}.allPartners`)}
                                    </strong>
                                </li>
                            </ul>
                            <p className="text-muted small mb-0">{t(`${STANDARD_EOS_PREFIX}.confirmWarning`)}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowStandardEosConfirm(false);
                                    setStandardEosError(null);
                                }}
                                disabled={standardEosLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleConfirmStandardEosSend} disabled={standardEosLoading}>
                                {standardEosLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t(`${STANDARD_EOS_PREFIX}.sending`)}
                                    </>
                                ) : (
                                    t(`${STANDARD_EOS_PREFIX}.confirmSend`)
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showEmailModal} onHide={handleCloseEmailModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiMail className="me-2" />
                                {t('pages.adminTripsDaily.sendEmailsTitle')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {emailError && (
                                <Alert
                                    variant="danger"
                                    className="mb-3"
                                    dismissible
                                    onClose={() => setEmailError(null)}
                                >
                                    <div className="d-flex align-items-start gap-2">
                                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                                        <div>
                                            <strong className="d-block mb-1">
                                                {t('pages.adminTripsDaily.errors.forbiddenTitle')}
                                            </strong>
                                            <span>{emailError}</span>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            <p className="mb-2">{t('pages.adminTripsDaily.sendEmailsMessage')}</p>
                            <p className="text-muted mb-2">{t('pages.adminTripsDaily.sendEmailsDescription')}</p>
                            <p className="mb-1"><strong>{t('pages.adminTripsDaily.targetedPartners')} :</strong></p>
                            {selectedPartnerNames.length > 0 ? (
                                <ul className="mb-0">
                                    {selectedPartnerNames.map((name) => (
                                        <li key={`confirm-email-${name}`}>{name}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mb-0 text-muted">{t('pages.adminTripsDaily.allPartnersTarget')}</p>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseEmailModal} disabled={emailLoading}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleSendEmail} disabled={emailLoading}>
                                {emailLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminTripsDaily.sending')}
                                    </>
                                ) : (
                                    t('pages.adminTripsDaily.confirmSend')
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showSmsModal} onHide={handleCloseSmsModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiMessageSquare className="me-2" />
                                {t('pages.adminTripsDaily.sendSmsTitle')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {smsError && (
                                <Alert variant="danger" className="mb-3" dismissible onClose={() => setSmsError(null)}>
                                    <strong>{t('pages.adminTripsDaily.error')}</strong> {smsError}
                                </Alert>
                            )}
                            <p className="mb-2">{t('pages.adminTripsDaily.sendSmsMessage')}</p>
                            <p className="text-muted mb-2">{t('pages.adminTripsDaily.sendSmsDescription')}</p>
                            <p className="mb-1"><strong>{t('pages.adminTripsDaily.targetedPartners')} :</strong></p>
                            {selectedPartnerNames.length > 0 ? (
                                <ul className="mb-0">
                                    {selectedPartnerNames.map((name) => (
                                        <li key={`confirm-sms-${name}`}>{name}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mb-0 text-muted">{t('pages.adminTripsDaily.allPartnersTarget')}</p>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseSmsModal} disabled={smsLoading}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="success" onClick={handleSendSms} disabled={smsLoading}>
                                {smsLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminTripsDaily.sendingSms')}
                                    </>
                                ) : (
                                    t('pages.adminTripsDaily.confirmSendSms')
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsDaily;
